import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useTenantStore } from '@/stores/tenants.ts'
import {
  Bot,
  Brain,
  FileText,
  Plus,
  RefreshCw,
} from 'lucide-react'
import {
  Badge,
  Button,
  DataTable,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  PageHeader,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  listDocuments,
  triggerSync,
  isDocumentOpen,
  formatDocNum,
  DOC_TYPE_LABELS,
  ALLOWED_CONVERSIONS,
  type CachedDocument,
  type DocType,
  type DocStatus,
} from '@/api/sapDocuments.ts'
import { DocumentDetailModal } from '@/components/documents/DocumentDetailModal.tsx'
import { DocumentConvertModal } from './DocumentConvertModal.tsx'
import { DocumentCreateModal } from './DocumentCreateModal.tsx'

const ALL_DOC_TYPES: DocType[] = ['quotation', 'order', 'delivery', 'invoice', 'credit']

function docStatusLabel(status: string | null, isClosed: boolean, isCancelled?: boolean): string {
  if (isCancelled) return 'Storniert'
  if (status === 'local-draft') return 'Entwurf'
  if (isClosed || status === 'bost_Close') return 'Geschlossen'
  if (status === 'bost_Open') return 'Offen'
  return status ?? '—'
}

function docStatusVariant(status: string | null, isClosed: boolean, isCancelled?: boolean): 'success' | 'warning' | 'neutral' | 'info' | 'danger' {
  if (isCancelled) return 'danger'
  if (status === 'local-draft') return 'warning'
  if (isClosed || status === 'bost_Close') return 'neutral'
  if (status === 'bost_Open') return 'success'
  return 'info'
}

function useDocColumns(
  onView: (doc: CachedDocument) => void,
  onConvertDirect: (doc: CachedDocument, target: DocType) => void,
  tenantMap: Map<string, string>,
  showTenantCol: boolean,
): ColumnDef<CachedDocument>[] {
  return useMemo(() => {
    const cols: ColumnDef<CachedDocument>[] = [
      {
        accessorKey: 'docDate',
        header: 'Datum',
        size: 90,
        cell: ({ row }) => {
          const d = row.original.docDate
          return d ? new Date(d).toLocaleDateString('de-DE') : '—'
        },
      },
      ...(showTenantCol ? [{
        id: 'tenant',
        header: 'Mandant',
        size: 110,
        cell: ({ row }: { row: { original: CachedDocument } }) => {
          const label = tenantMap.get(row.original.tenantId) ?? row.original.tenantId
          return <Badge variant="neutral" className="text-caption">{label}</Badge>
        },
      } satisfies ColumnDef<CachedDocument>] : []),
      {
        accessorKey: 'cardName',
        header: 'Kunde',
        cell: ({ row }) => row.original.cardName ?? row.original.cardCode ?? '—',
      },
      {
        id: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => {
          const { docStatus, isClosed, isCancelled } = row.original
          return (
            <Badge variant={docStatusVariant(docStatus, isClosed, isCancelled)}>
              {docStatusLabel(docStatus, isClosed, isCancelled)}
            </Badge>
          )
        },
      },
      {
        id: 'docType',
        header: 'Typ',
        size: 130,
        cell: ({ row }) => {
          const isAi = row.original.source === 'ai-draft' || row.original.source === 'ai-exported'
          return (
            <div className="flex items-center gap-1.5">
              <Badge variant="info" className="text-caption">
                {DOC_TYPE_LABELS[row.original.docType]}
              </Badge>
              {isAi && (
                <span
                  title={
                    row.original.source === 'ai-draft'
                      ? 'KI-Entwurf — noch nicht in SAP exportiert'
                      : 'Aus KI-Dialog erstellt — Verlauf einsehbar'
                  }
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-50 text-brand-600"
                  aria-label="KI-Angebot"
                >
                  <Bot className="w-3 h-3" />
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'sapDocNum',
        header: 'Belegnr.',
        size: 100,
        cell: ({ row }) => {
          if (row.original.source === 'ai-draft') {
            return <span className="font-mono text-caption text-amber-700">Entwurf</span>
          }
          return <span className="font-mono text-caption text-gray-700">{formatDocNum(row.original)}</span>
        },
      },
      {
        accessorKey: 'comments',
        header: 'Bemerkung',
        cell: ({ row }) => {
          const c = row.original.comments
          if (!c) return <span className="text-gray-400">—</span>
          return <span className="truncate max-w-[200px] block" title={c}>{c}</span>
        },
      },
      {
        accessorKey: 'docTotal',
        header: 'Betrag',
        size: 110,
        cell: ({ row }) => {
          const total = row.original.docTotal
          const currency = row.original.docCurrency ?? 'EUR'
          if (total == null) return '—'
          return `${total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
        },
      },
      {
        id: 'actions',
        header: '',
        size: 120,
        cell: ({ row }) => {
          const doc = row.original
          // ALLOWED_CONVERSIONS kennt nicht jeden DocType — z.B. legacy oder
          // serverseitig neu eingefuehrte Werte (sales_invoice u.a.) fuehren
          // sonst zu „can't access length of undefined" beim Render.
          const targets = ALLOWED_CONVERSIONS[doc.docType] ?? []
          const canConvert = doc.source !== 'ai-draft' && isDocumentOpen(doc) && targets.length > 0
          return (
            <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
              {canConvert && (
                <Dropdown>
                  <DropdownTrigger asChild>
                    <Button variant="ghost" size="xs">
                      Umwandeln ▾
                    </Button>
                  </DropdownTrigger>
                  <DropdownContent align="end">
                    {targets.map((t) => (
                      <DropdownItem key={t} onSelect={() => onConvertDirect(doc, t)}>
                        → {DOC_TYPE_LABELS[t]}
                      </DropdownItem>
                    ))}
                  </DropdownContent>
                </Dropdown>
              )}
            </div>
          )
        },
      },
    ]
    return cols
  }, [onView, onConvertDirect, tenantMap, showTenantCol])
}

export function DocumentsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Filter state
  const [selectedTypes, setSelectedTypes] = useState<DocType[]>([])
  const [statusFilter, setStatusFilter] = useState<DocStatus>('open')
  const [search, setSearch] = useState('')

  // Modal state
  const [viewDoc, setViewDoc] = useState<CachedDocument | null>(null)
  const [convertDoc, setConvertDoc] = useState<CachedDocument | null>(null)
  const [convertTarget, setConvertTarget] = useState<DocType | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createType, setCreateType] = useState<'quotation' | 'order' | 'invoice' | 'credit'>('quotation')

  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const tenants = useTenantStore((s) => s.tenants)
  const tenantMap = useMemo(
    () => new Map(tenants.map((t) => [t.id, t.code ?? t.name])),
    [tenants],
  )

  const queryKey = ['sap-documents', { types: selectedTypes, statusFilter, search, tenants: activeTenantIds }]

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      listDocuments({
        type: selectedTypes.length > 0 ? selectedTypes : undefined,
        status: statusFilter,
        search: search || undefined,
        pageSize: 200,
        tenantIds: activeTenantIds.length > 0 ? activeTenantIds : undefined,
      }),
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })

  const { mutate: doSync, isPending: syncing } = useMutation({
    mutationFn: triggerSync,
    onSuccess: (r) => {
      toast({ title: r.message ?? 'Sync abgeschlossen', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['sap-documents'] })
    },
    onError: (err) => {
      toast({ title: 'Sync fehlgeschlagen', description: err instanceof Error ? err.message : '', tone: 'danger' })
    },
  })

  // Wenn der Server meldet, dass der Cache komplett leer ist → Sync automatisch anstossen
  useEffect(() => {
    if (data?.cacheEmpty && !syncing) {
      doSync({})
    }
  }, [data?.cacheEmpty]) // eslint-disable-line react-hooks/exhaustive-deps

  // Alles (SAP-Beleg, KI-Entwurf, exportierte KI-Quote) wird im DocumentDetailModal
  // gezeigt. Bei KI-Quotes rendert das Modal die Dialog-Komponente embedded,
  // sodass kein Page-Sprung mehr passiert.
  const handleView = useCallback((doc: CachedDocument) => setViewDoc(doc), [])

  const handleConvertDirect = useCallback((doc: CachedDocument, target: DocType) => {
    setConvertDoc(doc)
    setConvertTarget(target)
  }, [])

  const handleConvertFromDetail = useCallback((doc: CachedDocument, target: DocType) => {
    setViewDoc(null)
    setConvertDoc(doc)
    setConvertTarget(target)
  }, [])

  const showTenantCol = activeTenantIds.length > 1
  const columns = useDocColumns(handleView, handleConvertDirect, tenantMap, showTenantCol)

  function toggleType(t: DocType) {
    setSelectedTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    )
  }

  const actions = (
    <div className="flex items-center gap-2">
      <IconButton
        icon={<Brain className="w-4 h-4" />}
        aria-label="KI-Wissensspeicher"
        variant="ghost"
        onClick={() => navigate('/einstellungen?tab=allgemein&subtab=wissensspeicher&wissensTab=wissensquellen')}
        title="KI-Wissensspeicher"
      />
      <IconButton
        icon={<RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />}
        aria-label="SAP-Sync anstoßen"
        variant="ghost"
        onClick={() => doSync({})}
        disabled={syncing}
        title="Aus SAP aktualisieren"
      />
      <Dropdown>
        <DropdownTrigger asChild>
          <Button variant="primary" size="sm" className="gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Neu
          </Button>
        </DropdownTrigger>
        <DropdownContent align="end">
          {(['quotation', 'order', 'invoice', 'credit'] as const).map((t) => (
            <DropdownItem
              key={t}
              onSelect={() => {
                setCreateType(t)
                setCreateOpen(true)
              }}
            >
              {DOC_TYPE_LABELS[t]}
            </DropdownItem>
          ))}
        </DropdownContent>
      </Dropdown>
    </div>
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dokumente"
        actions={actions}
      />

      {/* Filter bar */}
      <FilterBar>
        <Input
          placeholder="Suche nach Kunde, Belegnr., Bemerkung…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64 py-1 text-sm"
        />

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Type chips */}
        <FilterChip
          active={selectedTypes.length === 0}
          onClick={() => setSelectedTypes([])}
        >
          Alle Typen
        </FilterChip>
        {ALL_DOC_TYPES.map((t) => (
          <FilterChip
            key={t}
            active={selectedTypes.includes(t)}
            onClick={() => toggleType(t)}
            onRemove={selectedTypes.includes(t) ? () => toggleType(t) : undefined}
          >
            {DOC_TYPE_LABELS[t]}
          </FilterChip>
        ))}

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Status chips */}
        {(['all', 'open', 'closed', 'cancelled'] as DocStatus[]).map((s) => (
          <FilterChip
            key={s}
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          >
            {s === 'all' ? 'Alle Status' : s === 'open' ? 'Offen' : s === 'closed' ? 'Geschlossen' : 'Storniert'}
          </FilterChip>
        ))}

        {(data?.total != null) && (
          <span className="ml-auto text-caption text-gray-400">
            {data.total} Belege
          </span>
        )}
      </FilterBar>

      {/* Table */}
      {(isLoading || syncing) && <LoadingState label={syncing ? 'SAP-Belege werden synchronisiert…' : 'Dokumente werden geladen…'} />}
      {error && !syncing && (
        <ErrorState
          title="Fehler beim Laden"
          description={error instanceof Error ? error.message : 'Unbekannter Fehler'}
          onRetry={() => void queryClient.invalidateQueries({ queryKey })}
        />
      )}
      {!isLoading && !syncing && !error && (
        <DataTable
          data={data?.items ?? []}
          columns={columns}
          onRowClick={handleView}
          searchable={false}
          pagination={{ pageSize: 50 }}
          initialSorting={[{ id: 'docDate', desc: true }]}
          emptyState={
            <EmptyState
              icon={<FileText className="w-8 h-8 text-gray-300" />}
              title="Keine Dokumente gefunden"
              description={
                data?.cacheEmpty
                  ? 'SAP-Belege wurden noch nicht synchronisiert.'
                  : data?.total === 0 && !search && selectedTypes.length === 0
                    ? 'Keine Belege im Cache.'
                    : 'Keine Belege entsprechen den gewählten Filtern.'
              }
              action={
                (data?.cacheEmpty || (data?.total === 0 && !search && selectedTypes.length === 0)) ? (
                  <Button variant="primary" size="sm" onClick={() => doSync({})} disabled={syncing}>
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                    Jetzt synchronisieren
                  </Button>
                ) : undefined
              }
            />
          }
        />
      )}

      {/* Modals */}
      <DocumentDetailModal
        doc={viewDoc}
        open={!!viewDoc}
        onOpenChange={(o) => !o && setViewDoc(null)}
        onConvert={handleConvertFromDetail}
        onDeleted={() => {
          setViewDoc(null)
          void queryClient.invalidateQueries({ queryKey: ['sap-documents'] })
        }}
      />

      <DocumentConvertModal
        key={convertDoc?.sapDocEntry ?? convertDoc?.id}
        doc={convertDoc}
        targetType={convertTarget}
        open={!!convertDoc && !!convertTarget}
        onOpenChange={(o) => { if (!o) { setConvertDoc(null); setConvertTarget(null) } }}
        onSuccess={() => void queryClient.invalidateQueries({ queryKey: ['sap-documents'] })}
      />

      <DocumentCreateModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialType={createType}
        onSuccess={() => void queryClient.invalidateQueries({ queryKey: ['sap-documents'] })}
      />
    </div>
  )
}
